import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

/**
 * Auto-save hook for patient forms.
 * Saves form data as a draft every 30 seconds when the form has been modified.
 * Returns { lastSaved, isSaving, clearDraft }
 */
export function useAutoSave(formData, token, { enabled = true, isEditMode = false } = {}) {
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const prevDataRef = useRef(null);
  const hasNameRef = useRef(false);

  // Only auto-save new forms (not edit mode, which already persists via PUT)
  const shouldSave = enabled && !isEditMode;

  const saveDraft = useCallback(async (data) => {
    if (!token || !data.first_name || !data.last_name) return;
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/api/patients/draft`, data, {});
      setLastSaved(new Date());
    } catch (err) {
      console.warn('Auto-save failed:', err.message);
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  const clearDraft = useCallback(async () => {
    try {
      await axios.delete(`${API_URL}/api/patients/draft/current`, {});
    } catch (err) {
      console.warn('Draft clear failed:', err.message);
    }
  }, [token]);

  // Periodic auto-save
  useEffect(() => {
    if (!shouldSave) return;

    const interval = setInterval(() => {
      hasNameRef.current = !!(formData.first_name && formData.last_name);
      if (!hasNameRef.current) return;

      // Only save if data changed
      const current = JSON.stringify(formData);
      if (current !== prevDataRef.current) {
        prevDataRef.current = current;
        saveDraft(formData);
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(interval);
  }, [formData, shouldSave, saveDraft]);

  return { lastSaved, isSaving, clearDraft };
}

/**
 * Load a saved draft if it exists.
 * Returns the draft data or null.
 */
export async function loadDraft(token) {
  try {
    const { data } = await axios.get(`${API_URL}/api/patients/draft/current`, {});
    if (data.has_draft) return data.draft;
    return null;
  } catch {
    return null;
  }
}
